import React, { useEffect, useState } from "react";
import { http, formatError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Building2, Home, DoorOpen, Layers, Plus, School } from "lucide-react";
import { toast } from "sonner";

export default function HousingManager() {
  const [universities, setUniversities] = useState([]);
  const [universityId, setUniversityId] = useState("");
  const [tree, setTree] = useState({ communities: [], buildings: [], units: [], bedrooms: [] });

  useEffect(() => {
    http.get("/universities").then(({ data }) => {
      setUniversities(data);
      if (data.length && !universityId) setUniversityId(data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!universityId) return;
    loadTree();
  }, [universityId]);

  async function loadTree() {
    const { data } = await http.get(`/housing/tree?university_id=${universityId}`);
    setTree(data);
  }

  const uni = universities.find((u) => u.id === universityId);
  const totalBedrooms = tree.bedrooms.length;
  const totalUnits = tree.units.length;
  const totalBuildings = tree.buildings.length;
  const totalCommunities = tree.communities.length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Housing Management</div>
          <h2 className="font-display text-2xl font-bold text-primary" data-testid="housing-title">
            {uni?.name || "Select a university"}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <Select value={universityId} onValueChange={setUniversityId}>
            <SelectTrigger className="w-64" data-testid="housing-university-select"><SelectValue placeholder="University" /></SelectTrigger>
            <SelectContent>
              {universities.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <NewCommunityDialog universityId={universityId} onCreated={loadTree} />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={<School size={18} />} label="Communities" value={totalCommunities} />
        <Stat icon={<Building2 size={18} />} label="Buildings" value={totalBuildings} />
        <Stat icon={<Layers size={18} />} label="Units" value={totalUnits} />
        <Stat icon={<DoorOpen size={18} />} label="Bedrooms" value={totalBedrooms} />
      </div>

      <div className="space-y-3">
        {tree.communities.length === 0 && (
          <Card className="border-dashed"><CardContent className="p-10 text-center text-muted-foreground">
            No communities yet for this university. Create one to get started.
          </CardContent></Card>
        )}
        {tree.communities.map((c) => {
          const buildings = tree.buildings.filter((b) => b.community_id === c.id);
          const bedroomsInCommunity = tree.bedrooms.filter((r) => r.community_id === c.id).length;
          return (
            <Card key={c.id} className="border-border/60">
              <CardContent className="p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-display font-semibold text-primary text-lg">{c.name}</div>
                    <div className="text-xs text-muted-foreground">{c.address || "—"}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{c.billing_model}</Badge>
                    <Badge className="bg-secondary/10 text-secondary border border-secondary/20">{bedroomsInCommunity} bedrooms</Badge>
                    <NewBuildingDialog communityId={c.id} onCreated={loadTree} />
                  </div>
                </div>
                <div className="mt-3 space-y-2">
                  {buildings.length === 0 && <div className="text-xs text-muted-foreground pl-3">No buildings yet.</div>}
                  {buildings.map((b) => {
                    const units = tree.units.filter((u) => u.building_id === b.id);
                    return (
                      <div key={b.id} className="pl-3 border-l-2 border-secondary/20">
                        <div className="flex items-center justify-between py-1">
                          <div className="text-sm font-medium text-primary flex items-center gap-2"><Building2 size={14} />{b.name}</div>
                          <NewUnitDialog buildingId={b.id} onCreated={loadTree} />
                        </div>
                        <div className="grid sm:grid-cols-3 gap-2 pl-4">
                          {units.map((u) => {
                            const bedroomsInUnit = tree.bedrooms.filter((r) => r.unit_id === u.id);
                            return (
                              <div key={u.id} className="text-xs bg-muted/40 rounded-lg p-2">
                                <div className="flex items-center justify-between">
                                  <div className="font-medium text-primary">Unit {u.unit_number}</div>
                                  <Badge variant="outline" className="text-[10px]">{u.unit_type}</Badge>
                                </div>
                                <div className="text-muted-foreground mt-1">
                                  {bedroomsInUnit.length}/{u.total_bedrooms} bedrooms
                                </div>
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {bedroomsInUnit.map((r) => (
                                    <span key={r.id} className="px-1.5 py-0.5 bg-background rounded border border-border/60 text-[10px]">
                                      {r.bedroom_label}{r.current_student_id ? " ●" : ""}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <Card className="border-border/60"><CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div className="w-9 h-9 rounded-lg bg-primary/5 text-primary flex items-center justify-center">{icon}</div>
        <div className="font-display text-2xl font-bold text-primary">{value}</div>
      </div>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-2">{label}</div>
    </CardContent></Card>
  );
}

function NewCommunityDialog({ universityId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", address: "", billing_model: "b2c" });
  async function submit() {
    try {
      await http.post("/communities", { university_id: universityId, ...form });
      toast.success("Community created");
      setOpen(false); setForm({ name: "", address: "", billing_model: "b2c" });
      onCreated();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90" data-testid="new-community-btn"><Plus size={14} className="mr-1" /> Community</Button></DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>New apartment community</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-community-name" /></div>
          <div><Label className="text-xs">Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          <div><Label className="text-xs">Billing model</Label>
            <Select value={form.billing_model} onValueChange={(v) => setForm({ ...form, billing_model: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="b2c">Consumer (parent-pays)</SelectItem>
                <SelectItem value="b2b_net30">B2B (community-pays, net 30)</SelectItem>
                <SelectItem value="hybrid">Hybrid</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={submit} disabled={!form.name} className="w-full bg-primary hover:bg-primary/90" data-testid="new-community-save">Create</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NewBuildingDialog({ communityId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  async function submit() {
    try {
      await http.post("/buildings", { community_id: communityId, name });
      toast.success("Building added"); setOpen(false); setName(""); onCreated();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline"><Plus size={12} className="mr-1" /> Building</Button></DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>New building</DialogTitle></DialogHeader>
        <Input placeholder="Building name" value={name} onChange={(e) => setName(e.target.value)} data-testid="new-building-name" />
        <Button onClick={submit} disabled={!name} className="bg-primary hover:bg-primary/90">Create</Button>
      </DialogContent>
    </Dialog>
  );
}

function NewUnitDialog({ buildingId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ unit_number: "", floor: 1, unit_type: "1br", total_bedrooms: 1 });
  async function submit() {
    try {
      const { data: unit } = await http.post("/units", { building_id: buildingId, ...form });
      // Auto-generate bedrooms
      const labels = ["A", "B", "C", "D", "E"].slice(0, form.total_bedrooms);
      for (const l of labels) {
        await http.post("/bedrooms", { unit_id: unit.id, bedroom_label: `${form.unit_number}-${l}`, lease_type: "individual" });
      }
      toast.success(`Unit ${form.unit_number} created with ${form.total_bedrooms} bedroom(s)`);
      setOpen(false); setForm({ unit_number: "", floor: 1, unit_type: "1br", total_bedrooms: 1 }); onCreated();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="ghost" className="h-7"><Plus size={12} className="mr-1" /> Unit</Button></DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>New unit</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Unit #</Label><Input value={form.unit_number} onChange={(e) => setForm({ ...form, unit_number: e.target.value })} data-testid="new-unit-number" /></div>
          <div><Label className="text-xs">Floor</Label><Input type="number" value={form.floor} onChange={(e) => setForm({ ...form, floor: parseInt(e.target.value || "1") })} /></div>
          <div><Label className="text-xs">Type</Label>
            <Select value={form.unit_type} onValueChange={(v) => setForm({ ...form, unit_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["studio", "1br", "2br", "3br", "4br", "5br"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Bedrooms</Label><Input type="number" min={1} max={5} value={form.total_bedrooms} onChange={(e) => setForm({ ...form, total_bedrooms: parseInt(e.target.value || "1") })} data-testid="new-unit-bedrooms" /></div>
        </div>
        <Button onClick={submit} disabled={!form.unit_number} className="bg-primary hover:bg-primary/90 mt-2">Create unit + bedrooms</Button>
      </DialogContent>
    </Dialog>
  );
}
